import React, { useContext, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import CreateForm from "components/Form/CreateForm/CreateForm";
import useAPI from "../../../hooks/useAPI";
import { GlobalQueries } from "../../../hooks/useRefreshQuery";
import ProfilePage from "../ProfilePage";
import AuthContext from "../../../store/auth-context";
import formFields from "./formFields.json";
import { useTranslation } from "react-i18next";
import { printError } from "../../../helpers/apiError";
import Empty from "components/Empty/Empty";
import scss from "./MyProfilePage.module.scss";

const MyProfilePage = () => {
    const { t, i18n } = useTranslation();
    const api = useAPI();
    const apiPath = "/profile/user";
    const queryClient = useQueryClient();

    // Load all data
    const { data } = useQuery([GlobalQueries.Profile.MyProfile], () => api.get(apiPath));
    const { data: languageOptions } = useQuery(["languageOptions"], () => api.get("/options/global/languages"));
    const { data: config } = useQuery(["userProfileConfig"], () => api.get("/options/profile-page/user-profile/config"));

    const [updatedFormFields, setUpdatedFormFields] = useState(formFields);
    const [user, setUser] = useState();

    useEffect(() => {
        if (languageOptions && data) {
            const defaultLanguage = languageOptions.find((option) => option.is_default === true);
            const savedLanguage = localStorage.getItem("selectedLanguage");
            setUser({ ...data, language: savedLanguage ? savedLanguage : defaultLanguage.id });
            setUpdatedFormFields([
                ...formFields,
                {
                    field: "language",
                    label: "my_profile-default-form-field-language-label",
                    placeholder: "",
                    type: "select",
                    rows: false,
                    required: false,
                    disabled: false,
                    columns: true,
                    options: languageOptions,
                },
            ]);
        }
    }, [data, setUser, languageOptions]);
    const authCtx = useContext(AuthContext);
    const [loading, setLoading] = useState(false);

    // Refresh user data after change
    const handleUser = async () => {
        await api.get("/profile/user-data").then((response) => {
            if (response) {
                authCtx.refreshUser(response);
            }
        });
    };

    // Submit handler with query invalidation
    const submitHandler = () => {
        setLoading(true);
        api.post(apiPath, user)
            .then((response) => {
                toast.success(response?.message ? response.message : t("global-message-successfully_changed"));
                i18n.changeLanguage(user.language);
                localStorage.setItem("selectedLanguage", user.language);
                setUser({ ...response, language: user.language });
                handleUser();
                queryClient.invalidateQueries("ProfilePage");
                setLoading(false);
            })
            .catch((error) => toast.warn(printError(error) ? printError(error) : t("global-message-unknown_error")));
    };

    return (
        <ProfilePage>
            <h4 className={scss.title}>{t("my_profile-default-title")}</h4>

            {config && config?.active !== "1" && <Empty message={t("global-module_locked-title")} />}

            {user && config?.active === "1" && (
                <CreateForm
                    className={scss.form}
                    loading={loading}
                    data={user}
                    setData={setUser}
                    formFields={updatedFormFields}
                    submit={{ label: "my_profile-default-form-button-submit", action: submitHandler }}
                    formButtons={config?.user_can_save_profile_data === "1" ? true : false}
                />
            )}
        </ProfilePage>
    );
};

export default MyProfilePage;
